import { useCallback, useMemo, useState } from 'react';
import { useUser } from '@/hooks/useUser';
import { TUTORIAL_IDS, TUTORIAL_LOCAL_KEYS } from '@/constants/tutorial';

/**
 * チュートリアル進行状態を管理するフック
 *
 * 完了状態: ユーザープロフィールの completedTutorials リスト（サーバー保存）
 * 一時的なUI非表示: localStorage + React state（即時UI反映）
 */
export function useTutorialProgress() {
  const { user, updateProfile } = useUser();

  // localStorage の非表示フラグを React state で追跡（即時再レンダリング用）
  const [bannerDismissed, setBannerDismissed] = useState(
    () => getLocalFlagStatic(TUTORIAL_LOCAL_KEYS.BANNER_DISMISSED)
  );
  const [pulseShown, setPulseShown] = useState(
    () => getLocalFlagStatic(TUTORIAL_LOCAL_KEYS.PULSE_SHOWN)
  );

  const completedTutorials = useMemo<string[]>(
    () => user?.completedTutorials ?? [],
    [user?.completedTutorials]
  );

  // フォールバックキーはユーザー単位に分離する（共有端末・アカウント切替で
  // 別ユーザーが前ユーザーの完了状態を引き継がないように）。
  const userKey = user?.userId ?? 'guest';
  const fallbackKey = (id: string) => `tutorial:${userKey}:${id}_fallback`;

  // サーバの completedTutorials に加え、localStorage フォールバックも完了とみなす。
  // completeTutorial は保存前に同期でフォールバックを立てるため、サーバ反映前
  // （例: チュートリアルイベント削除→作成ページ再訪）でも再起動を防げる。
  const isTutorialCompleted = (id: string): boolean =>
    completedTutorials.includes(id) || getLocalFlagStatic(fallbackKey(id));

  const setLocalFlag = (key: string) => {
    try {
      localStorage.setItem(key, 'true');
    } catch {
      // ignore
    }
  };

  /** 指定チュートリアルを完了としてサーバーに保存 */
  const completeTutorial = useCallback(
    async (tutorialId: string) => {
      if (completedTutorials.includes(tutorialId)) return;

      // サーバ保存の成否に関わらず、まず同期でユーザー単位のローカル完了フラグを立てる。
      // これにより保存が settle する前でも isTutorialCompleted が true を返し、
      // 直後の画面遷移（例: チュートリアルイベント削除→作成ページ）での再起動を防ぐ。
      setLocalFlag(`tutorial:${userKey}:${tutorialId}_fallback`);

      const updated = [...completedTutorials, tutorialId];
      try {
        await updateProfile({ completedTutorials: updated });
      } catch {
        // サーバ保存失敗時もローカルフラグで完了扱いを維持
      }
    },
    [completedTutorials, updateProfile, userKey]
  );

  /** ウェルカムストーリー完了 */
  const completeWelcome = useCallback(() => {
    completeTutorial(TUTORIAL_IDS.WELCOME);
  }, [completeTutorial]);

  /** バナー非表示（セッション内のみ localStorage + state） */
  const dismissBanner = useCallback(() => {
    setLocalFlag(TUTORIAL_LOCAL_KEYS.BANNER_DISMISSED);
    setBannerDismissed(true);
  }, []);

  /** パルス演出表示済み（セッション内のみ localStorage + state） */
  const markPulseShown = useCallback(() => {
    setLocalFlag(TUTORIAL_LOCAL_KEYS.PULSE_SHOWN);
    setPulseShown(true);
  }, []);

  // ウェルカム完了判定（isTutorialCompleted がサーバー＋ユーザー単位フォールバックを判定）
  const welcomeCompleted = isTutorialCompleted(TUTORIAL_IDS.WELCOME);

  /** ウェルカムストーリーを表示すべきか */
  const shouldShowWelcome = !welcomeCompleted;

  /** ダッシュボードバナーを表示すべきか */
  const shouldShowBanner = welcomeCompleted && !bannerDismissed;

  /** パルス演出を表示すべきか */
  const shouldShowPulse = welcomeCompleted && !pulseShown;

  return {
    welcomeCompleted,
    shouldShowWelcome,
    shouldShowBanner,
    shouldShowPulse,
    completedTutorials,
    isTutorialCompleted,
    completeTutorial,
    completeWelcome,
    dismissBanner,
    markPulseShown,
  };
}

/** localStorage からフラグを取得（state初期値用の静的関数） */
function getLocalFlagStatic(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}
